import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Injector,
  afterNextRender,
  computed,
  effect,
  inject,
  input,
  linkedSignal,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  KiAuftrag,
  KiBefundAnzeige,
  KiBefundFeld,
  KiBefundSchwere,
  KiButtonState,
  KiGesendet,
  KiQualitaetAenderung,
  KiQualitaetBefund,
  KiQualitaetInhalt,
  KiQualitaetSchritt,
  KiQualitaetZiel,
  KiSchrittBefund,
  KiSperrgrund,
} from '../../../models/ki.model';
import { TestStep } from '../../../models/test-case.model';
import { KiRunService } from '../../../services/ki-run.service';
import { FIELD_LENGTHS } from '../../../shared/field-lengths';
import { KiBadgeComponent } from '../ki-badge/ki-badge.component';
import { KiBefundInlineComponent } from '../ki-befund-inline/ki-befund-inline.component';
import { KiButtonComponent } from '../ki-button/ki-button.component';
import { KiErgebnisAbgelaufenComponent } from '../ki-ergebnis-abgelaufen/ki-ergebnis-abgelaufen.component';
import { KiErsteNutzungComponent } from '../ki-erste-nutzung/ki-erste-nutzung.component';
import { KiFehlerComponent } from '../ki-fehler/ki-fehler.component';
import { fokusImBereichHalten, fokusVerloren } from '../ki-fokus';
import { KiGesendetComponent } from '../ki-gesendet/ki-gesendet.component';
import { KiLaeuftComponent } from '../ki-laeuft/ki-laeuft.component';
import { KiMarkComponent } from '../ki-mark/ki-mark.component';

/** page-ki.jsx:360: mehr Befunde zeigt der Check nicht. */
const MAX_BEFUNDE = 4;
const SCHWEREN: readonly KiBefundSchwere[] = ['PROBLEM', 'VERBESSERUNG', 'HINWEIS'];
const FELDER: readonly KiBefundFeld[] = ['ERWARTUNG', 'VORBEDINGUNG', 'SCHRITT'];
const ALLGEMEIN = 'ki.fehler.allgemein';

type BefundStand = 'offen' | 'verbessert' | 'gelassen';

interface BefundZeile extends KiQualitaetBefund {
  key: number;
  /** Schritt zum Zeitpunkt der Anfrage; ein danach gelöschter Schritt lässt sich nicht mehr verbessern. */
  schrittId: string | null;
  stand: BefundStand;
}

interface GesendeterSchritt extends KiQualitaetSchritt {
  id: string;
}

function istBefund(wert: unknown): wert is KiQualitaetBefund {
  const befund = wert as Partial<KiQualitaetBefund> | null;
  return (
    !!befund &&
    SCHWEREN.includes(befund.schwere as KiBefundSchwere) &&
    FELDER.includes(befund.feld as KiBefundFeld) &&
    typeof befund.text === 'string'
  );
}

/** Null, wenn das Ergebnis keine Befundliste trägt: das zeigt die Komponente als Fehler. */
function befundeAus(ergebnis: unknown): KiQualitaetBefund[] | null {
  const liste = (ergebnis as { befunde?: unknown } | null | undefined)?.befunde;
  if (!Array.isArray(liste)) return null;
  return liste
    .filter(istBefund)
    .sort((a, b) => (a.schrittNr ?? 0) - (b.schrittNr ?? 0))
    .slice(0, MAX_BEFUNDE);
}

/** Gezählt wie im Backend: Titel, Vorbedingung und je Schritt Text und Erwartung. */
function gesamtlaenge(titel: string, vorbedingung: string, schritte: readonly KiQualitaetSchritt[]): number {
  return schritte.reduce(
    (summe, schritt) => summe + schritt.schritt.length + schritt.erwartung.length,
    titel.length + vorbedingung.length,
  );
}

function zielVon(befund: BefundZeile): KiQualitaetZiel {
  return { feld: befund.feld, schrittId: befund.schrittId };
}

/** Kopfzeile und Schrittzeilen wie `KiTestfallBlock` im Backend. */
const BLOCK_KOPF = 'Qualitätscheck Testfall:';
const BLOCK_SCHRITT_ZEILE = /^\[(\d{1,9})] (Schritt|Erwartung):(.*)$/;

const einzeilig = (text: string): string => text.trim().replace(/\s+/g, ' ');

/** Schritte nach Nummer, wie sie an die KI gingen (maskiert, ggf. gekürzt). */
function gesendeterStand(texte: readonly string[] | undefined): Map<number, { schritt: string; erwartung: string }> {
  const stand = new Map<number, { schritt: string; erwartung: string }>();
  const block = texte?.find((text) => text.startsWith(BLOCK_KOPF));
  for (const zeile of block?.split('\n').slice(1) ?? []) {
    const treffer = BLOCK_SCHRITT_ZEILE.exec(zeile.trim());
    if (!treffer) continue;
    const nr = Number(treffer[1]);
    const text = treffer[3].trim();
    if (treffer[2] === 'Schritt') stand.set(nr, { schritt: text, erwartung: '' });
    else if (stand.has(nr)) stand.set(nr, { ...stand.get(nr)!, erwartung: text });
  }
  return stand;
}

/**
 * Ein gespeichertes Ergebnis kennt nur Schrittnummern. Zugeordnet wird ein heutiger Schritt nur, wenn er an
 * derselben Position mit Text und Erwartung so gesendet wurde; sonst träfe „Verbessern“ ein fremdes Feld.
 * Maskierte oder gekürzte Texte weichen ab und bleiben deshalb bewusst unzugeordnet.
 */
function wieGesendet(heute: readonly GesendeterSchritt[], texte: readonly string[] | undefined): GesendeterSchritt[] {
  const gesendet = gesendeterStand(texte);
  return heute.filter((schritt) => {
    const damals = gesendet.get(schritt.nr);
    return (
      !!damals && damals.schritt === einzeilig(schritt.schritt) && damals.erwartung === einzeilig(schritt.erwartung)
    );
  });
}

/** Schritt-Text wie in der Tabelle (Titel, sonst Aktion); Modulzeilen gehen nicht mit, zählen aber in `nr`. */
function zuSendendeSchritte(schritte: readonly TestStep[]): GesendeterSchritt[] {
  return schritte.flatMap((schritt, index) =>
    schritt.kind === 'MODULE_REF'
      ? []
      : [
          {
            id: schritt.id,
            nr: index + 1,
            schritt: schritt.title?.trim() || schritt.action || '',
            erwartung: schritt.expectedResult ?? '',
          },
        ],
  );
}

/**
 * Qualitätscheck der Testschritte eines Testfalls (page-ki.jsx:364-399). Ändert selbst nichts: Die Befunde stehen
 * als Kästen an den Schritten ({@link befundeAmSchritt}), die Vorbedingung meldet „Übernehmen“ über {@link verbessern}.
 */
@Component({
  selector: 'app-ki-qualitaets-check',
  imports: [
    KiBadgeComponent,
    KiBefundInlineComponent,
    KiButtonComponent,
    KiErgebnisAbgelaufenComponent,
    KiErsteNutzungComponent,
    KiFehlerComponent,
    KiGesendetComponent,
    KiLaeuftComponent,
    KiMarkComponent,
    TranslatePipe,
  ],
  providers: [KiRunService],
  templateUrl: './ki-qualitaets-check.component.html',
  styleUrl: './ki-qualitaets-check.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KiQualitaetsCheckComponent {
  protected readonly run = inject(KiRunService);
  private readonly translate = inject(TranslateService);
  private readonly host: HTMLElement = inject(ElementRef).nativeElement;
  private readonly document = inject(DOCUMENT);
  private readonly injector = inject(Injector);
  private readonly pruefenKnopf = viewChild(KiButtonComponent);
  /** Text der gesendeten Inhalte; zieht bei einem Sprachwechsel nach. */
  private readonly gesendetInhalte = toSignal(this.translate.stream('ki.qualitaet.gesendetInhalte'), {
    initialValue: '',
  });

  readonly projectId = input.required<string>();
  /** Null beim Anlegen: geprüft wird nur ein gespeicherter Testfall. */
  readonly testCaseId = input<string | null>(null);
  readonly titel = input('');
  readonly vorbedingung = input('');
  /** Aktueller, auch ungespeicherter Stand der Schritt-Tabelle. */
  readonly schritte = input<readonly TestStep[]>([]);
  /** Gespeicherte Anfrage aus dem Postfach-Absprung (`?kiAnfrage=`): wird gezeigt, nicht neu gestellt. */
  readonly anfrageId = input<string | null>(null);
  /** Nur Leserecht (PROJECT_READER): Befunde ansehen, aber nicht übernehmen, lassen oder erledigen. */
  readonly readOnly = input(false);
  readonly verbessern = output<KiQualitaetAenderung>();

  private readonly gesendeteSchritte = signal<readonly GesendeterSchritt[]>([]);
  /** Gespeichertes Ergebnis aus `?kiAnfrage`: der heutige Stand muss erst zum gesendeten passen. */
  private readonly geladen = signal(false);
  /** „Schließen“ gilt für das gezeigte Ergebnis; jeder neue Lauf zeigt seines wieder. */
  private readonly geschlossen = linkedSignal({ source: this.run.zustand, computation: () => false });
  /**
   * Lauf aus dem Check heraus gestartet: Während des Laufs verschwindet „Qualität prüfen“ samt Fokus. Ist er beim
   * Ergebnis noch nirgends, folgt er ihm; hat die Nutzerin ihn inzwischen woandershin gesetzt, bleibt er dort.
   */
  private fokusFolgtLauf = false;

  protected readonly sperrgrund = computed<KiSperrgrund | undefined>(() => {
    if (!this.testCaseId()) return 'ungespeichert';
    const schritte = zuSendendeSchritte(this.schritte());
    if (schritte.length === 0) return 'keine-schritte';
    // Das Backend begrenzt `nr`, und die zählt Modulzeilen mit.
    if (schritte[schritte.length - 1].nr > FIELD_LENGTHS.KI_QUALITAET_SCHRITTE_MAX) return 'zu-viele-schritte';
    if (gesamtlaenge(this.titel(), this.vorbedingung(), schritte) > FIELD_LENGTHS.KI_QUALITAET_ZEICHEN_MAX) {
      return 'zu-lang';
    }
    return undefined;
  });

  protected readonly buttonZustand = computed<{ state: KiButtonState; sperrgrund?: KiSperrgrund }>(() => {
    const sperrgrund = this.sperrgrund();
    return sperrgrund ? { state: 'gesperrt', sperrgrund } : { state: 'aktiv' };
  });

  /** Je Ergebnis neu; der Stand eines Befunds lebt nur hier und wird nicht gespeichert. */
  protected readonly befunde = linkedSignal<BefundZeile[] | null>(() => {
    const zustand = this.run.zustand();
    if (zustand.art !== 'fertig') return null;
    const befunde = befundeAus(zustand.anfrage.ergebnis);
    if (!befunde) return null;
    // Ungetrackt: Das Umschlüsseln nach dem Speichern darf den Stand der Befunde nicht auf „offen“ zurücksetzen.
    const gesendet = untracked(this.gesendeteSchritte);
    const zuordenbar = this.geladen() ? wieGesendet(gesendet, zustand.anfrage.gesendet?.texte) : gesendet;
    const schrittIds = new Map(zuordenbar.map((schritt) => [schritt.nr, schritt.id]));
    return befunde.map((befund, key) => ({
      ...befund,
      key,
      schrittId: befund.schrittNr === null ? null : (schrittIds.get(befund.schrittNr) ?? null),
      stand: 'offen',
    }));
  });

  /** Ein seit dem Lauf gelöschter Schritt nimmt seine Befunde mit; sie zählen nicht mehr (Story A12). */
  private readonly gezaehlt = computed<BefundZeile[]>(() => {
    const ids = new Set(this.schritte().map((schritt) => schritt.id));
    return (this.befunde() ?? []).filter((befund) => !befund.schrittId || ids.has(befund.schrittId));
  });

  private readonly offen = computed(() =>
    this.geschlossen() ? [] : this.gezaehlt().filter((befund) => befund.stand === 'offen'),
  );

  /** Für den Marker „KI-Befund“ am Feld Vorbedingung: nur offene Befunde des gezeigten Ergebnisses. */
  readonly offeneBefunde = computed<KiQualitaetZiel[]>(() => this.offen().map(zielVon));

  /** Offene Befunde, die an ihrer Schrittzeile stehen; die Tabelle zeigt sie dort als Kasten. */
  readonly befundeAmSchritt = computed<KiSchrittBefund[]>(() =>
    this.offen().flatMap(({ key, schrittId, schwere, feld, text, vorschlag }) =>
      schrittId ? [{ key, schrittId, schwere, feld: feld === 'VORBEDINGUNG' ? null : feld, text, vorschlag }] : [],
    ),
  );

  /**
   * Vorbedingung und Schrittbefunde, die sich keinem heutigen Schritt sicher zuordnen lassen, stehen unter der
   * Ergebnis-Leiste; Letztere nur mit „Erledigt“, weil „Übernehmen“ ein fremdes Feld träfe.
   */
  protected readonly befundeUnterLeiste = computed(() =>
    this.offen().flatMap((befund) => {
      if (befund.schrittId) return [];
      const vorbedingung = befund.schrittNr === null && befund.feld === 'VORBEDINGUNG';
      const anzeige: KiBefundAnzeige = {
        schwere: befund.schwere,
        feld: vorbedingung ? 'VORBEDINGUNG' : null,
        text: befund.text,
        vorschlag: befund.vorschlag,
        schrittNr: befund.schrittNr,
      };
      return [{ key: befund.key, anzeige }];
    }),
  );

  protected readonly zaehler = computed(() => ({
    gefunden: this.befunde()?.length ?? 0,
    anzahl: this.gezaehlt().length,
    offen: this.offen().length,
  }));

  protected readonly ansicht = computed<'ruhe' | 'laeuft' | 'ergebnis'>(() => {
    const art = this.run.zustand().art;
    if (art === 'laeuft') return 'laeuft';
    if (art === 'fertig' && this.befunde() && !this.geschlossen()) return 'ergebnis';
    return 'ruhe';
  });

  /** Das gespeicherte Ergebnis ist nach Ablauf der Aufbewahrung gelöscht (NEX-1570 AK7). */
  protected readonly abgelaufen = computed(() => {
    const zustand = this.run.zustand();
    return zustand.art === 'fertig' && zustand.anfrage.ergebnisGeloescht;
  });

  protected readonly fehlerKey = computed<string | null>(() => {
    const zustand = this.run.zustand();
    if (zustand.art === 'fehler') return zustand.textKey;
    return zustand.art === 'fertig' && !this.befunde() && !this.abgelaufen() ? ALLGEMEIN : null;
  });

  protected readonly gesendet = computed<KiGesendet | null>(() => {
    const zustand = this.run.zustand();
    if (zustand.art !== 'fertig') return null;
    return { ...zustand.anfrage.gesendet, inhalte: [this.gesendetInhalte()] };
  });

  constructor() {
    effect(() => {
      const ansicht = this.ansicht();
      const fehler = this.fehlerKey();
      untracked(() => {
        if (ansicht !== 'laeuft' && this.fokusFolgtLauf) this.fokusNachLauf(fehler !== null);
      });
    });
    effect(() => {
      const anfrageId = this.anfrageId();
      const testCaseId = this.testCaseId();
      if (!anfrageId || !testCaseId) return;
      untracked(() => {
        // Die Testfall-Seite rendert den Check erst mit geladenen Schritten; ihre Positionen ordnen die Befunde zu.
        this.gesendeteSchritte.set(zuSendendeSchritte(this.schritte()));
        this.geladen.set(true);
        this.run.laden(this.projectId(), anfrageId, { art: 'QUALITAETSCHECK', kontextId: testCaseId });
      });
    });
  }

  /**
   * „Übernehmen“ (page-testfaelle.jsx:938): Einen Schrittbefund hat die Tabelle schon ins Feld geschrieben; nur den
   * Vorschlag zur Vorbedingung meldet der Check selbst über {@link verbessern}.
   */
  uebernehmen(key: number): void {
    const befund = this.offenerBefund(key);
    if (!befund) return;
    if (befund.schrittNr === null && befund.feld === 'VORBEDINGUNG' && befund.vorschlag) {
      this.verbessern.emit({ feld: 'VORBEDINGUNG', wert: befund.vorschlag });
    }
    this.setzeStand(key, 'verbessert');
  }

  /** Nach dem Speichern tragen neue Schritte Server-IDs; ohne Umbenennen fielen ihre Befunde wie gelöschte heraus. */
  idsUmschluesseln(mapping: Readonly<Record<string, string>>): void {
    if (Object.keys(mapping).length === 0) return;
    const neu = (id: string): string => mapping[id] ?? id;
    this.gesendeteSchritte.update((schritte) => schritte.map((schritt) => ({ ...schritt, id: neu(schritt.id) })));
    this.befunde.update(
      (zeilen) =>
        zeilen?.map((zeile) => (zeile.schrittId ? { ...zeile, schrittId: neu(zeile.schrittId) } : zeile)) ?? null,
    );
  }

  /** „Lassen“ bzw. „Erledigt“: blendet den Befund aus, ohne ein Feld zu ändern. */
  lassen(key: number): void {
    if (!this.offenerBefund(key)) return;
    this.setzeStand(key, 'gelassen');
  }

  /** Der Kasten unter der Leiste verschwindet mit seinen Knöpfen; der Fokus bleibt im Check. */
  protected unterLeisteErledigt(key: number, uebernehmen: boolean): void {
    fokusImBereichHalten(this.host, this.injector, () =>
      this.host.querySelector<HTMLElement>('[data-testid="ki-qualitaet-schliessen"]')?.focus(),
    );
    if (uebernehmen) this.uebernehmen(key);
    else this.lassen(key);
  }

  protected aktuellerWert(anzeige: KiBefundAnzeige): string {
    return anzeige.feld === 'VORBEDINGUNG' ? this.vorbedingung() : '';
  }

  protected schliessen(): void {
    fokusImBereichHalten(this.host, this.injector, () => this.pruefenKnopf()?.fokussieren());
    this.geschlossen.set(true);
  }

  pruefen(): void {
    const testCaseId = this.testCaseId();
    if (!testCaseId || this.sperrgrund()) return;
    const schritte = zuSendendeSchritte(this.schritte());
    const inhalt: KiQualitaetInhalt = {
      titel: this.titel(),
      vorbedingung: this.vorbedingung(),
      schritte: schritte.map(({ nr, schritt, erwartung }) => ({ nr, schritt, erwartung })),
    };
    const auftrag: KiAuftrag = {
      projectId: this.projectId(),
      art: 'qualitaetscheck',
      kontextTyp: 'TESTCASE',
      kontextId: testCaseId,
      start: { pfad: `test-cases/${encodeURIComponent(testCaseId)}/ki/qualitaet`, body: inhalt },
    };
    this.gesendeteSchritte.set(schritte);
    this.geladen.set(false);
    this.fokusFolgtLauf = this.host.contains(this.document.activeElement);
    this.run.starten(auftrag);
  }

  private offenerBefund(key: number): BefundZeile | undefined {
    if (this.readOnly()) return undefined;
    return this.offen().find((befund) => befund.key === key);
  }

  private setzeStand(key: number, stand: BefundStand): void {
    this.befunde.update((zeilen) => zeilen?.map((zeile) => (zeile.key === key ? { ...zeile, stand } : zeile)) ?? null);
  }

  private fokusNachLauf(fehler: boolean): void {
    afterNextRender(
      () => {
        this.fokusFolgtLauf = false;
        if (!fokusVerloren(this.document)) return;
        const ziel = this.host.querySelector<HTMLElement>(
          fehler ? '[data-testid="ki-fehler-erneut"]' : '[data-testid="ki-qualitaet-schliessen"]',
        );
        if (ziel) ziel.focus();
        else this.pruefenKnopf()?.fokussieren();
      },
      { injector: this.injector },
    );
  }
}
